import type {
	BreakContribution,
	BreakRulesConfig,
	HoursRateConfig,
	HoursRateContribution,
	OvertimeConfig,
	OvertimeContribution,
	OvertimeKind,
} from "../../../utils/models";
import { OvertimeConfigType } from "../../../utils/models";
import { indicesOfRule, OVERTIME_TYPES_BY_KIND } from "../../team/WageSettings/deepLinkRowMatchers";
import { type BreakRulesCellRow, clockTime, type HoursRateCellRow, type OvertimeCellRow } from "../../team/WageSettings/wageRuleCellText";

/*
 * DAT-906 · A breakdown line's rule against today's Wage Settings: which stored row it is, whether that row
 * still says what priced the shift, and — where today's rows cannot be read — the row as it was captured.
 */

/** Stored rows are `DECIMAL(10, 2)`; captured figures are 4dp. Equal on the cent is equal. */
const CENT = 0.005;
const sameNumber = (a: number, b: number): boolean => Math.abs(a - b) < CENT;

const sameList = (a: readonly string[] | undefined, b: readonly string[]): boolean => {
	// DEFAULT: an unset stored list means "All", the same as `[]` (see the config models).
	const left = new Set(a ?? []);
	return left.size === new Set(b).size && b.every((value) => left.has(value));
};

/** A tri-state: `undefined` on a stored row and `null` in a captured scope both mean "not restricted". */
const sameFlag = (a: boolean | null | undefined, b: boolean | null): boolean => (a ?? null) === b;

/** "18:00" and "18:00:00" are one time; no time is "all day". */
const clockOf = (time: string | null | undefined): string | null => (time ? clockTime(time) : null);

const sameAudience = (
	row: { roles?: readonly string[]; jobIds?: readonly string[]; union?: readonly string[]; isUnion?: boolean | null },
	scope: { roles: string[]; jobIds: string[]; union: string[]; isUnion: boolean | null },
): boolean =>
	sameList(row.roles, scope.roles) && sameList(row.jobIds, scope.jobIds) && sameList(row.union, scope.union) && sameFlag(row.isUnion, scope.isUnion);

const sameName = (a: string, b: string): boolean => a.trim() === b.trim();

/** Where in its table (row indices) a rule line's stored row is: its id, else every row still carrying its name. */
export const hoursRateMatches = (rows: readonly HoursRateConfig[], c: HoursRateContribution): number[] => indicesOfRule(rows, c.ruleId ?? undefined, c.name);

/** Whether a rule of this type can produce the line's overtime kind (an `all` rule, daily or weekly). */
const producesKind = (row: OvertimeConfig, c: OvertimeContribution): boolean => OVERTIME_TYPES_BY_KIND[c.otKind].includes(row.type);

/**
 * …for overtime, by id and then by name like the others: a rule whose type was changed since (weekly to
 * daily) is still that rule, shown as changed, not as gone. Among several rows sharing the name, the ones
 * of a type that can produce the line's kind are the rule; only when none is, the renamed-type ones are.
 */
export const overtimeMatches = (rows: readonly OvertimeConfig[], c: OvertimeContribution): number[] => {
	const named = indicesOfRule(rows, c.ruleId ?? undefined, c.name);
	const ofKind = named.filter((index) => producesKind(rows[index], c));
	return ofKind.length > 0 ? ofKind : named;
};

export const breakRulesMatches = (rows: readonly BreakRulesConfig[], c: BreakContribution): number[] => indicesOfRule(rows, c.ruleId ?? undefined, c.name);

/** Whether today's row would no longer price the shift the way the captured rule did. */
export const hoursRateChanged = (row: HoursRateConfig, c: HoursRateContribution): boolean => {
	const { scope } = c;
	const same =
		sameName(row.name, c.name) &&
		row.bonusType === c.valueType &&
		sameNumber(row.bonusVal, c.value) &&
		clockOf(row.startTime) === clockOf(scope.startTime) &&
		clockOf(row.endTime) === clockOf(scope.endTime) &&
		sameFlag(row.weekend, scope.weekend) &&
		sameFlag(row.isCharge, scope.isCharge) &&
		sameList(row.trainingStatuses, scope.trainingStatuses) &&
		sameList(row.employmentTypes, scope.employmentTypes) &&
		sameList(row.agency, scope.agency) &&
		sameAudience(row, scope);
	return !same;
};

export const overtimeChanged = (row: OvertimeConfig, c: OvertimeContribution): boolean => {
	const { scope } = c;
	const same =
		sameName(row.name, c.name) &&
		producesKind(row, c) &&
		row.rateType === c.valueType &&
		sameNumber(row.rate, c.value) &&
		sameNumber(row.hoursLimit, c.thresholdHours) &&
		sameList(row.employmentType, scope.employmentTypes) &&
		(row.consecutiveWindow ?? null) === scope.consecutiveWindow &&
		(row.consecutiveDaysFrom ?? null) === scope.consecutiveDaysFrom &&
		(row.consecutiveDaysTo ?? null) === scope.consecutiveDaysTo &&
		sameAudience(row, scope);
	return !same;
};

/** A line stored before the rule's type and start were captured cannot tell whether those changed. */
const sameIfCaptured = <T>(live: T, captured: T | null): boolean => captured === null || live === captured;

export const breakRulesChanged = (row: BreakRulesConfig, c: BreakContribution): boolean => {
	const same =
		sameName(row.name, c.name) &&
		sameNumber(row.breakTime, c.breakMinutes) &&
		sameNumber(row.breakLimit, c.appliesOverHours) &&
		sameIfCaptured(row.type, c.ruleType) &&
		(c.startsAfterHours === null || sameNumber(row.breakStart ?? row.breakLimit, c.startsAfterHours)) &&
		sameList(row.employmentTypes, c.scope.employmentTypes) &&
		sameAudience(row, c.scope);
	return !same;
};

/** The captured rule as a row of its table, for when today's rows cannot be read. */
export const capturedHoursRateRow = ({ name, scope, valueType, value }: HoursRateContribution): HoursRateCellRow => ({
	name,
	startTime: scope.startTime,
	endTime: scope.endTime,
	weekend: scope.weekend,
	isCharge: scope.isCharge,
	roles: scope.roles,
	jobIds: scope.jobIds,
	trainingStatuses: scope.trainingStatuses,
	employmentTypes: scope.employmentTypes,
	agency: scope.agency,
	union: scope.union,
	isUnion: scope.isUnion,
	bonusVal: value,
	bonusType: valueType,
});

/**
 * The type a captured overtime line reads as. The line records the limit that was passed, not the rule's
 * type, so an `all` rule shows as the kind it produced here.
 */
const OVERTIME_TYPE_OF_KIND: Readonly<Record<OvertimeKind, OvertimeConfigType>> = {
	daily: OvertimeConfigType.daily,
	weekly: OvertimeConfigType.weekly,
	consecutive: OvertimeConfigType.consecutive_day,
};

export const capturedOvertimeRow = ({ name, scope, valueType, value, otKind, thresholdHours }: OvertimeContribution): OvertimeCellRow => ({
	name,
	hoursLimit: thresholdHours,
	roles: scope.roles,
	jobIds: scope.jobIds,
	employmentType: scope.employmentTypes,
	union: scope.union,
	isUnion: scope.isUnion,
	type: OVERTIME_TYPE_OF_KIND[otKind],
	consecutiveDaysFrom: scope.consecutiveDaysFrom,
	consecutiveDaysTo: scope.consecutiveDaysTo,
	rate: value,
	rateType: valueType,
});

/**
 * A captured break line as a break-rules row. A line stored before the rule's type and break start were
 * captured has neither: the caller shows those two as not recorded, so the placeholders here only satisfy
 * the row's shape.
 */
export const capturedBreakRulesRow = ({ name, scope, breakMinutes, appliesOverHours, ruleType, startsAfterHours }: BreakContribution): BreakRulesCellRow => ({
	name,
	breakLimit: appliesOverHours,
	breakStart: startsAfterHours ?? undefined,
	roles: scope.roles,
	jobIds: scope.jobIds,
	employmentTypes: scope.employmentTypes,
	union: scope.union,
	isUnion: scope.isUnion,
	type: ruleType ?? "addition",
	breakTime: breakMinutes,
});
